import {
    Box,
    BoxProps,
    Button,
    ButtonProps,
    CircularProgress,
    Grid,
    GridProps,
    Link,
    LinkProps,
    Skeleton,
    Tooltip,
    useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import { MdOutlineDraw, MdAdsClick } from "react-icons/md";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import * as Styles from "pages/requests/stepsIndividual/standalone-step5/step5.styles";

import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import DataBlock from "components/datablocks/DataBlock";
import { ApplicationState } from "types/applicationState";

import * as documentService from "services/document-service";
import * as applicationService from "services/application-service";
import * as connectiveService from "services/connective-service";
import FileUpload from "components/formsUI/FileUpload";
import ManualSignModal from "./modals/manualSign.modal";
import { useSettingsContext } from "contexts/settingsContext";
import ConditionalWrapper from "components/conditionalWrapper";
import { useTenantContext } from "contexts/tenantContext";

interface Props {
    showMessage: MessageBox;
    title: string;
    changeActiveSteps: any;
}

const Content = ({ title, changeActiveSteps, showMessage }: Props) => {
    let navigate = useNavigate();
    const { applicationId, completionHash } = useParams();
    const { currentTenant, hiddenTenant } = useTenantContext();
    const { getSetting } = useSettingsContext();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const sessionId = searchParams.get("SessionID");
    const status = searchParams.get("Status");
    const packageExternalReference = searchParams.get("PackageExternalReference");

    const isRedirectedFromConnective =
        sessionId !== null && status !== null && packageExternalReference !== null;

    const theme = useTheme();
    const { t: _t } = useTranslation();
    const [applicationState, setApplicationState] = React.useState<
        Promise<void> | string
    >(async () => {
        await applicationService.getApplicationStateAsync(applicationId as string).then((response) => {
            setApplicationState(response);
        });
    });

    const t = (key: string, tenant: boolean = false): string => {
        var multiple = getSetting<boolean>('useVertrouwelijk');
        const options: any = {
            count: multiple ? 2 : 1,
        }
        if (tenant) {
            options['replace'] = {
                email: currentTenant ? currentTenant?.email : hiddenTenant ? hiddenTenant?.email : '',
                tel: currentTenant ? currentTenant?.tel : hiddenTenant ? hiddenTenant?.tel : '',
            }
        }
        return _t(key, options);
    }

    const [base64data, setBase64data] = React.useState<string[]>([]);
    const [documentName, setDocumentName] = React.useState<string[]>([]);
    const [signUrl, setSignUrl] = React.useState("");
    const [documentCreated, setDocumentCreated] = React.useState(false);
    const [documentCreationTime, setDocumentCreationTime] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [allowSubmit, setAllowSubmit] = React.useState(false);
    const [readySign, setReadySign] = React.useState<boolean>(false);
    const [manualSign, setManualSign] = React.useState<boolean>(false);
    const [enableFileUpload, setEnableFileUpload] = React.useState<boolean>(false);
    const [openManualSign, setOpenManualSign] = React.useState<boolean>(false);
    const handleOpenManualSign = () => setOpenManualSign(true);
    const handleCloseManualSign = () => setOpenManualSign(false);

    // Error
    const [showErrorMessage, setShowErrorMessage] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");

    const showError = (message: string) => {
        setErrorMessage(message);
        setShowErrorMessage(true);
    };

    const [errorCreating, setErrorCreating] = React.useState(false);
    const [errorUpdating, setErrorUpdating] = React.useState(false);
    const [errorGetting, setErrorGetting] = React.useState(false);
    const [errorSubmit, setErrorSubmit] = React.useState(false);


    async function fetchDataAndUpdate() {
        setShowErrorMessage(false);
        setErrorUpdating(false);

        if (status) {
            await applicationService.changeApplicationStateAsync(applicationId as string, status).then(() => {
                let statePascal = status?.charAt(0).toUpperCase() + status?.substring(1).toLocaleLowerCase();
                setApplicationState(statePascal);
            })
                .catch((error: AxiosError<Error>) => {
                    setErrorUpdating(true);
                    showError("requestIndividual.step5.errorUpdating");
                    throw error;
                });
        }

        await applicationService.getApplicationPackageId(applicationId as string)
            .then(async (packageId: any) => {
                return packageId;
            })
            .then(async (packageId: string) => {
                await connectiveService.getPackageStatus(packageId)
                    .then((response) => {
                        var packageData = JSON.parse(JSON.stringify(response));
                        if (documentName[0] === "" || documentName[0] === undefined) {
                            setAllowSubmit(true);
                            setDocumentName(packageData.Documents.map((doc: any) => doc.Name));
                            setDocumentCreationTime(packageData.CreationDate);
                        }
                    });
                setDocumentCreated(true);
                if (status === "SIGNED") {
                    changeActiveSteps(5);
                }
            })
            .catch((error: AxiosError<Error>) => {
                if (error.response?.status == 404)
                    navigate("/aanvraag_veiligheidsverificatie_ingediend");
                setErrorUpdating(true);
                showError("requestIndividual.step5.errorUpdating");
            });
    };

    async function createDocToSign() {
        setShowErrorMessage(false);
        setErrorCreating(false);
        if (completionHash !== undefined) {
            await documentService.updateDocumentToSign(applicationId as string, completionHash)
                .then((res: ConnectiveResponse) => {
                    // var connectiveResponseObject = JSON.parse(JSON.stringify(res));
                    setBase64data(res.pdfBase64);
                    setDocumentName(res.pdfFileName);
                    setSignUrl(res.signUrl);
                    setDocumentCreated(true);
                })
                .catch((error: AxiosError<Error>) => {
                    setErrorCreating(true);
                    showError("requestIndividual.step5.errorCreating");
                });
            return;
        }
        await documentService.createDocumentToSign(applicationId as string)
            .then((res: ConnectiveResponse) => {
                // var connectiveResponseObject = JSON.parse(JSON.stringify(res));
                setBase64data(res.pdfBase64);
                setDocumentName(res.pdfFileName);
                setSignUrl(res.signUrl);
                setDocumentCreated(true);
            })
            .catch((error: AxiosError<Error>) => {
                setErrorCreating(true);
                showError("requestIndividual.step5.errorCreating");
            });
    }

    async function getPackage() {
        setShowErrorMessage(false);
        setErrorGetting(false);
        var errorStatusGet = false;
        await applicationService.getApplicationPackageId(applicationId as string)
            .then((packageId: any) => {
                return packageId;
            })
            .then(async (packageId: string) => {
                await connectiveService.getPackageStatus(packageId)
                    .then((response) => {
                        var packageData = JSON.parse(JSON.stringify(response));
                        setDocumentName(packageData.Documents.map((doc: any) => doc.Name));
                    })
                    .catch((error: AxiosError<Error>) => {
                        errorStatusGet = true;
                    });
                return packageId;
            })
            .then(async (packageId: string) => {
                await connectiveService.getPackageRemainingActions(packageId).then(
                    (response: string[]) => {
                        setSignUrl(response[0]);
                        setDocumentCreated(true);
                        if (applicationState === "Signed") {
                            setAllowSubmit(true);
                        }
                    }
                );
            })
            .catch((error: AxiosError<Error>) => {
                if (!errorStatusGet) {
                    setErrorGetting(true);
                    showError("requests.error");
                } else {
                    createDocToSign();
                }
            });
    }

    async function fetchData() {
        switch (applicationState) {
            case ApplicationState.Announced:
                await createDocToSign();
                break;
            case ApplicationState.Pending:
            case ApplicationState.Signed:
            case ApplicationState.Rejected:
                await getPackage();
                break;
        }
    };

    // const createAVNumber = useCallback(
    //   async (applicationId: string) => {},
    //   [applicationId]
    // );

    const [onInitAfterCallback, setOnInitAfterCallback] = React.useState(false);
    useEffect(() => {
        if (isRedirectedFromConnective && !onInitAfterCallback) {
            setOnInitAfterCallback(true);
            fetchDataAndUpdate();
        }
        else if (!documentCreated && !isRedirectedFromConnective) {
            fetchData();
        }
    }, [applicationId, documentCreated, applicationState]);

    function handleOnSignClick() {
        if (signUrl) {
            window.location.href = signUrl;
        }
    }

    const downloadApplicationToSign = () => {
        if (completionHash !== undefined) {
            documentService.getDocumentToSign(applicationId as string, completionHash).then((res: Blob) => {
                if (res !== undefined) {
                    var fileUrl = URL.createObjectURL(res);
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.download = `${t("settings.documents.downloadRequestName")}.pdf`;
                    link.click();
                }
            })
        } else {
            documentService.getDocumentToSign(applicationId as string).then((res: Blob) => {
                if (res !== undefined) {
                    var fileUrl = URL.createObjectURL(res);
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.download = `${t("settings.documents.downloadRequestName")}.pdf`;
                    link.click();
                }
            })
        }
    }

    const downloadVertrouwelijkheidsovereenkomstToSign = () => {
        documentService.getVertrouwelijkDocumentToSign(applicationId as string, completionHash).then((res: Blob) => {
            if (res !== undefined) {
                var fileUrl = URL.createObjectURL(res);
                const link = document.createElement("a");
                link.href = fileUrl;
                link.download = `${t("settings.documents.downloadVertrouwelijkName")}.pdf`;
                link.click();
            }
        })
    }

    function handleErrorButtonClick() {
        if (errorCreating) {
            createDocToSign();
        }
        else if (errorUpdating) {
            fetchDataAndUpdate();
        }
        else if (errorGetting) {
            getPackage();
        }
        else if (errorSubmit) {
            submitApplication();
        }
    }

    function submitApplication() {
        setShowErrorMessage(false);
        setErrorSubmit(false);
        setLoading(true);
        console.log(manualSign, getSetting<boolean>('useConnective'))
        // (!manualSign && getSetting<boolean>('useConnective'))
        if (manualSign || !getSetting<boolean>('useConnective')) {
            applicationService.finishApplicationManualAsync(applicationId as string)
                .then((res: void) => {
                    navigate("/aanvraag_veiligheidsverificatie_ingediend");
                    setLoading(false);
                })
                .catch((error: AxiosError<Error>) => {
                    setErrorSubmit(true);
                    showError(`${t("requestIndividual.step5.errorUpdating")}`);
                    setLoading(false);
                });
        } else {
            applicationService.finishApplicationAsync(applicationId as string)
                .then((res: void) => {
                    navigate("/aanvraag_veiligheidsverificatie_ingediend");
                    setLoading(false);
                })
                .catch((error: AxiosError<Error>) => {
                    setErrorSubmit(true);
                    showError(`${t("requestIndividual.step5.errorUpdating")}`);
                    setLoading(false);
                });
        }
    }


    // TODO : Move to Global Styles
    const gridContainerProps: GridProps = {
        container: true,
        spacing: 2,
        minHeight: { sm: 400, md: 450 },
    };

    const gridItemFullProps: GridProps = {
        item: true,
        xs: 12,
    };

    const titleProps: BoxProps = {
        component: "h1",
        sx: Styles.titleCSS(theme),
    };

    const linkProps = (name: string): LinkProps => {
        console.log(name)
        switch (name.toLowerCase()) {
            case "aanvraagformulier":
                return {
                    onClick: downloadApplicationToSign,
                    href: '#',
                    underline: 'hover',
                    color: 'inherit',
                }
            case "vertrouwelijkheidsovereenkomst":
                return {
                    onClick: downloadVertrouwelijkheidsovereenkomstToSign,
                    href: '#',
                    underline: 'hover',
                    color: 'inherit',
                }
            default:
                return {
                    href: '#',
                    underline: 'hover',
                    color: 'inherit',
                }
        }
    }

    const handleTextChange = (): string => {
        if (manualSign) return t("requestIndividual.step5.messages.documentReady.manualText");
        if (!getSetting<boolean>('useConnective')) return t("requestIndividual.step5.messages.documentReady.manualText");
        return t("requestIndividual.step5.messages.documentReady.text");
    }

    const getNoBorder = (): boolean => {
        if (readySign) return false;
        return applicationState !== "Signed" && applicationState !== "Failed";
    }

    const buttonProps = (buttonName: string): ButtonProps => {
        if (buttonName === "previous") {
            return {
                variant: "contained",
                sx: Styles.buttonCSS(theme, buttonName),
            };
        } else {
            return {
                type: "submit",
                variant: "contained",
                sx: Styles.buttonCSS(theme, buttonName),
            };
        }
    };

    return (
        <Box sx={Styles.containerCSS(theme)}>
            <Grid {...gridContainerProps}>
                <Grid {...gridItemFullProps}>
                    <Box {...titleProps}>{title}</Box>
                    {/* {applicationState !== ApplicationState.Signed && <Box {...titleProps}>{title}</Box>} */}
                    {/* {applicationState === ApplicationState.Signed && <Box {...titleProps}>{t("requestIndividual.step5.messages.documentSigned.title")}</Box>} */}

                    {applicationState !== ApplicationState.Signed &&
                        <Box sx={{ textAlign: "justify", whiteSpace: "pre-line" }}>
                            {t(`requestIndividual.step5.warning`, true)}
                        </Box>
                    }
                    {applicationState === ApplicationState.Signed &&
                        <Box sx={{ textAlign: "justify", whiteSpace: "pre-line" }}>
                            {t("requestIndividual.step5.messages.documentSigned.text")}
                        </Box>
                    }

                    {(applicationState === ApplicationState.Signed || readySign) &&
                        <Box sx={{ textAlign: "center", width: "100%", marginTop: 2 }}>
                            <Button variant="contained" onClick={() => { submitApplication(); }} disabled={(!(allowSubmit || readySign)) || loading}>{t("requestIndividual.step5.buttonSubmit")}</Button>
                        </Box>
                    }
                    {/* <InfoMessageBox
            title={t(`requestIndividual.step5.warningTitle`)}
            text={t(`requestIndividual.step5.warning`)}
            icon={HistoryEduIcon}
            isCollapsable={false}
            isCollapsed={false}
          /> */}
                </Grid>
                <Grid {...gridItemFullProps} sx={{ textAlign: "center" }}>
                    {(loading || (!allowSubmit && applicationState === ApplicationState.Signed)) && <CircularProgress sx={{ mt: 2 }} />}
                    {showErrorMessage &&
                        <Box color="error.main" sx={{ width: "100%", textAlign: "center", mt: 2 }}>
                            <Box>{t(errorMessage)}</Box>
                            <Button sx={{ marginTop: "5px" }} color="error" variant="outlined" onClick={() => { handleErrorButtonClick(); }}>{t("errors.buttonTryAgain")}</Button>
                        </Box>
                    }
                </Grid>
                <Grid {...gridItemFullProps}>
                    {applicationState !== ApplicationState.Signed &&
                        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <h3>{t(`requestIndividual.step5.subTitle`)}</h3>
                        </Box>
                    }

                    {/* Document field with status */}
                    {!documentCreated && (applicationState === ApplicationState.Announced || applicationState === ApplicationState.Pending) &&
                        <Box sx={Styles.downloadButtonCSS(theme)}>
                            <AutorenewIcon sx={{
                                width: "20px",
                                height: "20px",
                                fontSize: "20px",
                                transform: "horizontal-flip",
                                animation: "spin 4s linear infinite",
                                "@keyframes spin": { "100%": { transform: "rotate(360deg)" } },
                                color: "#00467d",
                            }} />

                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    borderRadius: "5px",
                                    my: "20px",
                                    width: "70%",
                                    height: '20px'
                                }}
                            />
                            <Box>
                                <Button variant="contained" sx={{ borderRadius: "10px" }} disabled>
                                    {t("requestIndividual.step5.loading")}
                                </Button>
                            </Box>
                        </Box>
                    }

                    {documentCreated && (applicationState === ApplicationState.Announced || applicationState === ApplicationState.Pending) &&
                        <Box sx={Styles.downloadButtonCSS(theme)}>
                            <Box sx={{ maxWidth: '50%' }}>
                                {documentName.map((doc, index) => (
                                    <ConditionalWrapper
                                        key={index}
                                        condition={manualSign || !getSetting<boolean>('useConnective')}
                                        wrapper={(e) => <Link {...linkProps(doc.split(' ')[0])} children={e} />}
                                    >
                                        <Box sx={Styles.downloadFilesCSS(theme)}>
                                            {/* <InsertDriveFileIcon sx={{ px: "10px" }} /> */}
                                            <PictureAsPdfIcon sx={{ px: "10px" }} />
                                            <Tooltip title={doc ? doc.includes('Vertrouwelijkheidsovereenkomst') ? doc.replace("Veiligheidsverificatie PZA", "") : doc : 'unknow.pdf'} arrow disableInteractive>
                                                <Box sx={{ margin: '5px 0 5px 0', padding: '0', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {`${doc ? 
                                                        (doc.startsWith("Aanvraagformulier") || doc.startsWith("Formulaire de demande")) ? t("Aanvraagformulier") :
                                                        (doc.startsWith("Vertrouwelijkheidsovereenkomst") || doc.startsWith("Accord de confidentialité")) ? t("Vertrouwelijkheidsovereenkomst") :
                                                        doc.split(" - ")[0] 
                                                        : "Unknown"}`
                                                    }
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                    </ConditionalWrapper>
                                ))}
                            </Box>
                            {(!manualSign && getSetting<boolean>('useConnective')) ?
                                <Button
                                    variant="contained"
                                    startIcon={<MdAdsClick />}
                                    sx={{ borderRadius: "10px", padding: '10px 25px', minWidth: 'fit-content' }}
                                    onClick={handleOnSignClick}
                                    disabled={!signUrl}
                                >
                                    {t("requestIndividual.step5.buttonSign")}
                                </Button> :
                                <Button
                                    startIcon={<MdOutlineDraw />}
                                    variant="contained"
                                    sx={{ borderRadius: "10px", padding: '10px 25px', minWidth: 'fit-content', visibility: readySign ? 'hidden' : '' }}
                                    onClick={handleOpenManualSign}
                                >
                                    {t("requestIndividual.step5.buttonSign")}
                                </Button>
                            }
                        </Box>
                        // <Loaded documentName={documentName} signUrl={signUrl} /> 
                    }

                    {documentCreated && applicationState === ApplicationState.Signed &&
                        <Box sx={Styles.downloadButtonCSS(theme)}>
                            <Box>
                                {documentName.map((doc, index) => (
                                    <Box key={index} sx={Styles.downloadFilesCSS(theme)}>
                                        <CheckCircleIcon sx={{ color: "green", px: "10px", fontSize: "20px" }} />
                                        <Tooltip title={doc ? doc.includes('Vertrouwelijkheidsovereenkomst') ? doc.replace("Veiligheidsverificatie PZA", "") : doc : 'unknow.pdf'} arrow disableInteractive>
                                            <Box sx={{ margin: '5px 0 5px 0', padding: '0', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {`${doc ? 
                                                    (doc.startsWith("Aanvraagformulier") || doc.startsWith("Formulaire de demande")) ? t("Aanvraagformulier") :
                                                    (doc.startsWith("Vertrouwelijkheidsovereenkomst") || doc.startsWith("Accord de confidentialité")) ? t("Vertrouwelijkheidsovereenkomst") :
                                                    doc.split(" - ")[0] 
                                                    : "Unknown"}`
                                                }
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Box>

                            {/* <DownloadIcon sx={{ color: "blue", "&:hover": { color: "#4d4dff", cursor: "pointer" }, px: "10px", fontSize: "30px" }}/> */}
                        </Box>
                    }



                    <Box sx={{ mb: "70px" }}>
                        <DataBlock
                            type="top"
                            title={t("requestIndividual.step5.messages.documentPending.title")}
                            text={t("requestIndividual.step5.messages.documentPending.text")}
                            date={dayjs().format("DD MMM, YYYY HH:mm")}
                            state={(documentCreated || applicationState === ApplicationState.Signed) ? "Success" : "Pending"}
                        />

                        {(documentCreated || applicationState === ApplicationState.Signed) &&
                            <DataBlock
                                type="mid"
                                title={t("requestIndividual.step5.messages.documentReady.title")}
                                text={handleTextChange()}
                                date={dayjs().format("DD MMM, YYYY HH:mm")}
                                state={(documentCreated || applicationState === ApplicationState.Signed) ? "Success" : "Pending"}
                                midBorderBottom={getNoBorder()}
                                linkProps={(!manualSign && getSetting<boolean>('useConnective')) ? { link: t('requestIndividual.step5.messages.documentReady.linkText'), manualSign: manualSign, linkClick: setManualSign } : undefined}
                            >
                                {/* {manualSign &&
                                    <FileUpload
                                        completionHash={completionHash}
                                        showMessage={showMessage}
                                        setFilesUploaded={(_) => { setReadySign(_); setEnableFileUpload(_); }}
                                        isStep5={true}
                                        title={''}
                                        textUpload={t('requestIndividual.step5.messages.documentReady.uploadText')}
                                        disable={enableFileUpload}
                                    />
                                } */}
                            </DataBlock>
                        }

                        {readySign &&
                            <DataBlock
                                type="bottom"
                                title={t('requestIndividual.step5.messages.documentManualSinged.title')}
                                text={t('requestIndividual.step5.messages.documentManualSinged.text')}
                                date={dayjs().format("DD MMM, YYYY HH:mm")}
                                state="Success"
                            />
                        }

                        {(applicationState === ApplicationState.Signed || applicationState === "Failed") &&
                            <DataBlock
                                type="bottom"
                                title={applicationState === ApplicationState.Signed ? t("requestIndividual.step5.messages.documentSigned.title") : t("requestIndividual.step5.messages.documentRejected.title")}
                                text={applicationState === ApplicationState.Signed ? t("requestIndividual.step5.messages.documentSigned.text") : t("requestIndividual.step5.messages.documentRejected.text")}
                                date={dayjs().format("DD MMM, YYYY HH:mm")}
                                state={applicationState === ApplicationState.Signed ? "Success" : "Failed"}
                            />
                        }
                    </Box>
                </Grid>
            </Grid>
            <ManualSignModal
                open={openManualSign}
                onClose={handleCloseManualSign}
                showMessage={showMessage}
                onSubmit={setReadySign}
                downloadApplicationToSign={downloadApplicationToSign}
                downloadVertrouwelijkheidsovereenkomstToSign={downloadVertrouwelijkheidsovereenkomstToSign}
            />
        </Box>
    );
};
export default Content;
